import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function isWebAuthnAvailable() {
  return typeof window !== "undefined" && !!window.PublicKeyCredential && window.isSecureContext;
}

function b64uToBytes(value) {
  const pad = "=".repeat((4 - (value.length % 4)) % 4);
  const s = atob(value.replaceAll("-", "+").replaceAll("_", "/") + pad);
  return Uint8Array.from(s, (c) => c.charCodeAt(0));
}

function bytesToB64u(value) {
  const bytes = value instanceof ArrayBuffer ? new Uint8Array(value) : value;
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}

function prepRegOptions(o) {
  return {
    ...o,
    challenge: b64uToBytes(o.challenge),
    user: { ...o.user, id: b64uToBytes(o.user.id) },
    excludeCredentials: (o.excludeCredentials ?? []).map((x) => ({ ...x, id: b64uToBytes(x.id) })),
  };
}

function prepAuthOptions(o) {
  return {
    ...o,
    challenge: b64uToBytes(o.challenge),
    allowCredentials: (o.allowCredentials ?? []).map((x) => ({ ...x, id: b64uToBytes(x.id) })),
  };
}

function regResponse(c) {
  const r = c.response;
  return {
    id: c.id,
    rawId: bytesToB64u(c.rawId),
    type: c.type,
    response: {
      clientDataJSON: bytesToB64u(r.clientDataJSON),
      attestationObject: bytesToB64u(r.attestationObject),
      transports: r.getTransports?.() ?? ["internal"],
    },
    clientExtensionResults: c.getClientExtensionResults(),
  };
}

function authResponse(c) {
  const r = c.response;
  return {
    id: c.id,
    rawId: bytesToB64u(c.rawId),
    type: c.type,
    response: {
      clientDataJSON: bytesToB64u(r.clientDataJSON),
      authenticatorData: bytesToB64u(r.authenticatorData),
      signature: bytesToB64u(r.signature),
      userHandle: r.userHandle ? bytesToB64u(r.userHandle) : null,
    },
    clientExtensionResults: c.getClientExtensionResults(),
  };
}

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function enrollFingerprint() {
  if (!isWebAuthnAvailable()) throw new Error("Este dispositivo o navegador no admite huella (requiere HTTPS)");
  const { data: options } = await axios.post(`${API}/webauthn/registration/options`, {}, { headers: authHeaders() });
  const credential = await navigator.credentials.create({ publicKey: prepRegOptions(options) });
  if (!credential) throw new Error("Registro cancelado");
  const { data } = await axios.post(`${API}/webauthn/registration/verify`, regResponse(credential), { headers: authHeaders() });
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  if (user?.username) localStorage.setItem("centinela_webauthn_user", user.username);
  return data;
}

export async function loginWithFingerprint(username) {
  if (!isWebAuthnAvailable()) throw new Error("Este dispositivo o navegador no admite huella (requiere HTTPS)");
  const { data: options } = await axios.post(`${API}/webauthn/authentication/options`, { username });
  const userId = options.user_id;
  delete options.user_id;
  const credential = await navigator.credentials.get({ publicKey: prepAuthOptions(options) });
  if (!credential) throw new Error("Acceso cancelado");
  const { data } = await axios.post(`${API}/webauthn/authentication/verify`, {
    user_id: userId,
    credential: authResponse(credential),
  });
  if (data?.user?.username) localStorage.setItem("centinela_webauthn_user", data.user.username);
  return data;
}

export async function getFingerprintStatus() {
  const { data } = await axios.get(`${API}/webauthn/status`, { headers: authHeaders() });
  return data;
}

export async function removeFingerprint() {
  const { data } = await axios.delete(`${API}/webauthn/credentials`, { headers: authHeaders() });
  localStorage.removeItem("centinela_webauthn_user");
  return data;
}

export function rememberedFingerprintUser() {
  return localStorage.getItem("centinela_webauthn_user") || "";
}
